import { z } from "zod";
import { retentionModeSchema } from "./models.ts";

/**
 * Instance model connections and the account allowlist (openspec change
 * `model-connections-cross-account`, ADR 0017 slice 2b): what the operator console reads and
 * writes. The external ID ESI generates per connection is never accepted as input.
 */

/** The fixed name of the role a model account creates for ESI (Decided (a)). */
export function modelAccessRoleName(env: string): string {
  return `esi-model-access-${env}`;
}

export const modelAccountIdSchema = z.string().regex(/^\d{12}$/, "A 12-digit AWS account ID");
export const MODEL_CONNECTION_ID_PATTERN = /^[a-z][a-z0-9-]{1,30}$/;
export const modelConnectionIdSchema = z
  .string()
  .regex(MODEL_CONNECTION_ID_PATTERN, "Lowercase letters, digits and hyphens, 2 to 31 characters")
  .refine((id) => id !== "default", "`default` is the deployment account's own connection");
const labelSchema = z.string().trim().min(1).max(80);

export const MODEL_PROBE_STATUSES = [
  "ok",
  /** The perimeter updater has not admitted the account yet. */
  "not_applied",
  "assume_denied",
  "model_denied",
  "error",
] as const;
export const modelProbeStatusSchema = z.enum(MODEL_PROBE_STATUSES);
export type ModelProbeStatus = z.infer<typeof modelProbeStatusSchema>;

/** The model account's invocation logging, when its role lets ESI read it. */
export const MODEL_LOGGING_STATUSES = ["off", "on", "unknown"] as const;
export const modelLoggingStatusSchema = z.enum(MODEL_LOGGING_STATUSES);
export type ModelLoggingStatus = z.infer<typeof modelLoggingStatusSchema>;

export const instanceModelConnectionSchema = z.object({
  id: z.string(),
  accessPath: z.literal("bedrock-assume-role"),
  accountId: z.string(),
  roleArn: z.string(),
  retention: retentionModeSchema,
  enabled: z.boolean(),
  /** Route order among the registered connections; `default` always comes after them. */
  position: z.int(),
  label: z.string(),
  probeStatus: modelProbeStatusSchema.nullable(),
  probedAt: z.string().nullable(),
  loggingStatus: modelLoggingStatusSchema.nullable(),
  /** A successful probe newer than the last change: enabling is allowed. */
  probeFresh: z.boolean(),
  /** The account is on the allowlist, bound to the instance, and the perimeter admits it. */
  accountApplied: z.boolean(),
  /**
   * The account is bound to a tenant (model-picking D2): the connection cannot be probed or
   * enabled. Optional for one release (canary).
   */
  accountTenantBound: z.boolean().optional(),
  changedAt: z.string(),
  createdAt: z.string(),
  createdBy: z.string(),
  updatedAt: z.string(),
});
export type InstanceModelConnection = z.infer<typeof instanceModelConnectionSchema>;

/**
 * Whom an allowlisted account serves (openspec change `model-picking`, D2): instance connections
 * only, or the connections of one named tenant only.
 */
export const MODEL_ACCOUNT_BINDINGS = ["instance", "tenant"] as const;
export type ModelAccountBoundTo = (typeof MODEL_ACCOUNT_BINDINGS)[number];

export const modelAccountBindingSchema = z.discriminatedUnion("boundTo", [
  z.strictObject({ boundTo: z.literal("instance") }),
  z.strictObject({ boundTo: z.literal("tenant"), tenantId: z.uuid() }),
]);
export type ModelAccountBinding = z.infer<typeof modelAccountBindingSchema>;

export const modelAccountSchema = z.object({
  accountId: z.string(),
  label: z.string(),
  boundTo: z.enum(MODEL_ACCOUNT_BINDINGS),
  /** The bound tenant (`boundTo: "tenant"`), its ID and name; null for an instance account. */
  tenant: z.object({ id: z.string(), name: z.string() }).nullable(),
  addedBy: z.string(),
  createdAt: z.string(),
  /** When the perimeter updater admitted it; null while pending. */
  appliedAt: z.string().nullable(),
  /**
   * When the purge of its tenant unbound it (#406): it serves no one until an operator rebinds
   * it; `tenant` still names the purged tenant. Null (or absent, from the previous release) while
   * it serves its binding.
   */
  unboundAt: z.string().nullable().optional(),
});
export type ModelAccount = z.infer<typeof modelAccountSchema>;

/** What the model account's administrator creates (a USER step of the runbook). */
export const modelConnectionSetupSchema = z.object({
  roleName: z.string(),
  roleArn: z.string(),
  externalId: z.string(),
  sourceIdentity: z.string(),
  trustPolicy: z.record(z.string(), z.unknown()),
  permissionsPolicy: z.record(z.string(), z.unknown()),
});
export type ModelConnectionSetup = z.infer<typeof modelConnectionSetupSchema>;

/** Why the perimeter updater's last run failed: past the documents' capacity, or an AWS write. */
export const PERIMETER_ERRORS = ["capacity_exceeded", "write_failed"] as const;
export type PerimeterError = (typeof PERIMETER_ERRORS)[number];

/** The perimeter's state against the allowlist (the console's banner). */
export const modelPerimeterStatusSchema = z.object({
  /** Allowlisted, not admitted yet: their connections do not route. */
  pendingAdds: z.array(z.string()),
  /** Removed, the network path not narrowed yet (no connection routes to them). */
  pendingRemovals: z.array(z.string()),
  error: z.enum(PERIMETER_ERRORS).nullable(),
  diverged: z.boolean(),
  /** Diverged for longer than a run takes, or the last run failed: shown as a warning. */
  overdue: z.boolean(),
  /** The updater's last successful run, and its last run. */
  appliedAt: z.string().nullable(),
  attemptedAt: z.string().nullable(),
});
export type ModelPerimeterStatus = z.infer<typeof modelPerimeterStatusSchema>;

export const modelConnectionsOverviewSchema = z.object({
  connections: z.array(instanceModelConnectionSchema),
  accounts: z.array(modelAccountSchema),
  /** The allowlist's size against what the runtimes' permission policy can hold. */
  capacity: z.object({ used: z.int(), max: z.int() }),
  /** The deployment account, which no connection may point at. */
  deploymentAccount: z.string().nullable(),
  /** The allowlist against the perimeter the updater last applied. */
  perimeter: modelPerimeterStatusSchema,
});
export type ModelConnectionsOverview = z.infer<typeof modelConnectionsOverviewSchema>;

export const createModelConnectionRequestSchema = z.strictObject({
  id: modelConnectionIdSchema,
  accountId: modelAccountIdSchema,
  retention: retentionModeSchema,
  label: labelSchema,
});
export type CreateModelConnectionRequest = z.infer<typeof createModelConnectionRequestSchema>;

export const createModelConnectionResponseSchema = z.object({
  connection: instanceModelConnectionSchema,
  setup: modelConnectionSetupSchema,
});

export const patchModelConnectionRequestSchema = z
  .strictObject({
    enabled: z.boolean().optional(),
    retention: retentionModeSchema.optional(),
    label: labelSchema.optional(),
    position: z.int().min(0).max(10_000).optional(),
    /** Enabling a connection whose model account logs invocations needs this confirmation. */
    confirmLogging: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).some((k) => k !== "confirmLogging"), "Nothing to change");
export type PatchModelConnectionRequest = z.infer<typeof patchModelConnectionRequestSchema>;

export const addModelAccountRequestSchema = z.strictObject({
  accountId: modelAccountIdSchema,
  label: labelSchema,
  /** Absent: an instance account (the console before `model-picking` sends none). */
  binding: modelAccountBindingSchema.optional(),
});
export type AddModelAccountRequest = z.infer<typeof addModelAccountRequestSchema>;

/** `PATCH /api/operator/model-accounts/:accountId`: bind the account elsewhere (step-up). */
export const rebindModelAccountRequestSchema = z.strictObject({
  binding: modelAccountBindingSchema,
});
export type RebindModelAccountRequest = z.infer<typeof rebindModelAccountRequestSchema>;
